import { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { PageHead, Card, Pill, HealthDot, Btn, Loading, Err } from "./_shared";

export default function ModelProviders() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [testing, setTesting] = useState(null);
  const [results, setResults] = useState({});

  const load = () => api.get("/api/admin/model-providers").then((d) => setItems(d.items)).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const runTest = async (key) => {
    setTesting(key);
    try {
      const res = await api.post(`/api/admin/model-providers/${key}/test`);
      setResults((p) => ({ ...p, [key]: res }));
      await load();
    } catch (e) {
      setResults((p) => ({ ...p, [key]: { ok: false, status: "error", message: e.message } }));
    } finally { setTesting(null); }
  };

  if (err) return <Err msg={err} />;
  if (!items) return <Loading />;

  return (
    <div data-testid="admin-model-providers">
      <PageHead title="Model providers" sub={`${items.length} providers in the gateway`} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
        {items.length === 0 ? (
          <Card><div style={{ color: "var(--pl-text-muted)", fontSize: 12 }}>No providers seeded yet.</div></Card>
        ) : items.map((p) => (
          <Card key={p.key}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--pl-aqua)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800 }}>
                  {p.key}
                </div>
                <div style={{ fontSize: 18, fontWeight: 900, marginTop: 4 }}>{p.name || p.key}</div>
                <div style={{ fontSize: 11, color: "var(--pl-text-muted)", marginTop: 6, lineHeight: 1.5 }}>
                  {p.description || "—"}
                </div>
              </div>
              <Pill tone={p.enabled ? "success" : "muted"}>{p.enabled ? "enabled" : "disabled"}</Pill>
            </div>
            <div style={{ marginTop: 12, display: "grid", gap: 6, fontSize: 12 }}>
              <KV k="Priority" v={p.priority ?? "—"} />
              <KV k="Capabilities" v={(p.capabilities || []).join(", ") || "—"} />
              <KV k="Health" v={<HealthDot status={p.health_status || "not_configured"} />} />
              <KV k="Latency p95" v={p.latency_p95_ms ? `${p.latency_p95_ms} ms` : "—"} />
            </div>
            <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
              <Btn testId={`provider-test-${p.key}`} tone="ghost" onClick={() => runTest(p.key)} disabled={testing === p.key}>
                {testing === p.key ? "Testing…" : "Run health check"}
              </Btn>
              {results[p.key] && (
                <Pill tone={results[p.key].ok ? "success" : "danger"}>
                  {results[p.key].status}
                </Pill>
              )}
            </div>
            {results[p.key]?.message && (
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--pl-text-muted)" }}>{results[p.key].message}</div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px dashed var(--pl-border)" }}>
      <span style={{ color: "var(--pl-text-muted)" }}>{k}</span>
      <span style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 700 }}>{v}</span>
    </div>
  );
}
